import { useState, useEffect } from 'react';
import {
  Settings,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  Store,
  ChevronDown,
  ChevronRight,
  Snowflake,
  ShieldAlert,
  ClipboardList,
  Calculator,
  Banknote,
  PackageCheck,
  MousePointerClick,
  History,
} from 'lucide-react';
import { financeApi, storeApi } from '../lib/api';
import type { FinanceCollectorStatus } from '../lib/api';
import { financeChannelApi } from '../lib/finance-channel-api';
import { FinanceAttributionStorePanel } from '../components/finance/FinanceAttributionStorePanel';
import { FinanceJournalSettingsModal } from '../components/FinanceJournalSettingsModal';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StatusTreeNode {
  id: string;
  name: string;
  color: string;
  parentId: string | null;
  storeId: string | null;
  children: StatusTreeNode[];
}

interface StoreInfo {
  id: string;
  name: string;
  platform: string;
}

interface SettingsResponse {
  completedStatusMap: Record<string, string[]>;
  legacyStatuses: string[];
  stores: StoreInfo[];
  statusTree: StatusTreeNode[];
  cashflowSnapshotEnabled: boolean;
  vatThresholdEnabled: boolean;
  vatThresholdLimit: number;
  vatThresholdNotifyPercent: number;
  vatThresholdStartDate: string | null;
  vatThresholdPriorRevenue: number;
  includeZusTaxes: boolean;
  taxDeductions?: { incomeTax?: boolean; healthIns?: boolean; socialIns?: boolean };
  codPendingEnabled: boolean;
  codSettlementDays: number;
  codTransitDays: number;
  codExcludeReturns: boolean;
  codUseExcludedStatuses?: boolean;
  codExcludedStatusMap?: Record<string, string[]>;
}

// ---------------------------------------------------------------------------
// CollapsibleSection - reusable themed section with icon + title + toggle
// ---------------------------------------------------------------------------
function CollapsibleSection({
  icon,
  title,
  description,
  defaultOpen = false,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={`rounded-xl overflow-hidden border transition-all ${
      open
        ? 'border-accent-primary/30 shadow-neon-cyan'
        : 'border-border hover:border-border-strong'
    } bg-surface-raised/50`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-surface-hover/30 transition-colors text-left"
      >
        <div className={`flex-shrink-0 ${open ? 'text-accent-primary' : 'text-text-secondary'}`}>{icon}</div>
        <div className="flex-1 min-w-0">
          <span className="text-text-primary font-semibold">{title}</span>
          <p className="text-xs text-text-muted mt-0.5">{description}</p>
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-accent-primary flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-text-secondary flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-accent-primary/20">
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusTreeCheckboxes - renders nested checkboxes with depth-based indentation
// ---------------------------------------------------------------------------
function StatusTreeCheckboxes({
  nodes,
  selectedIds,
  onToggle,
  depth = 0,
}: {
  nodes: StatusTreeNode[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  depth?: number;
}) {
  return (
    <>
      {nodes.map((node) => {
        const isSelected = selectedIds.includes(node.id);
        return (
          <div key={node.id}>
            <label
              className={`flex items-center gap-3 py-2 px-3 rounded-lg cursor-pointer transition-colors ${
                isSelected
                  ? 'bg-success/10'
                  : 'hover:bg-surface-hover/40'
              }`}
              style={{ paddingLeft: `${depth * 20 + 12}px` }}
            >
              <div
                onClick={(e) => { e.preventDefault(); onToggle(node.id); }}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${
                  isSelected
                    ? 'bg-success border-success'
                    : 'border-border bg-surface-hover hover:border-border-strong'
                }`}
              >
                {isSelected && <CheckCircle className="w-3.5 h-3.5 text-surface-base" />}
              </div>
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: node.color || '#6b7280' }}
              />
              <span className={`text-sm ${isSelected ? 'text-text-primary font-medium' : 'text-text-secondary'}`}>
                {depth > 0 && <span className="text-text-disabled mr-1">-</span>}
                {node.name}
              </span>
            </label>
            {node.children?.length > 0 && (
              <StatusTreeCheckboxes
                nodes={node.children}
                selectedIds={selectedIds}
                onToggle={onToggle}
                depth={depth + 1}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// StoreStatusSection - one card per store
// ---------------------------------------------------------------------------
function StoreStatusSection({
  store,
  statusTree,
  selectedIds,
  onToggle,
}: {
  store: StoreInfo;
  statusTree: StatusTreeNode[];
  selectedIds: string[];
  onToggle: (storeId: string, statusId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const relevantStatuses = statusTree.filter(
    (s) => !s.storeId || s.storeId === store.id
  );

  return (
    <div className="bg-surface-hover/30 border border-border-subtle rounded-xl overflow-hidden mt-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-hover/50 transition-colors text-left"
      >
        <Store className="w-4 h-4 text-accent-primary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-text-primary font-medium text-sm">{store.name}</span>
          <span className="ml-2 text-xs text-text-muted uppercase">{store.platform}</span>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <span className="text-xs bg-success/15 text-success px-2 py-0.5 rounded-full">
              {selectedIds.length} wybranych
            </span>
          )}
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-text-secondary" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-text-secondary" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-border-subtle">
          {relevantStatuses.length > 0 ? (
            <div className="mt-2">
              <StatusTreeCheckboxes
                nodes={relevantStatuses}
                selectedIds={selectedIds}
                onToggle={(statusId) => onToggle(store.id, statusId)}
              />
            </div>
          ) : (
            <p className="text-sm text-text-muted py-3 px-3">
              Brak statusów. Utwórz je w Logistyka &rarr; Statusy zamówień.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export function FinanceSettingsPage() {
  // Status mapping state
  const [completedStatusMap, setCompletedStatusMap] = useState<Record<string, string[]>>({});
  const [stores, setStores] = useState<StoreInfo[]>([]);
  const [statusTree, setStatusTree] = useState<StatusTreeNode[]>([]);

  // Snapshot state
  const [snapshotEnabled, setSnapshotEnabled] = useState(false);

  // VAT threshold state
  const [vatEnabled, setVatEnabled] = useState(false);
  const [vatLimit, setVatLimit] = useState(200000);
  const [vatNotifyPercent, setVatNotifyPercent] = useState(80);
  const [vatStartDate, setVatStartDate] = useState('');
  const [vatPriorRevenue, setVatPriorRevenue] = useState(0);
  const [vatStatus, setVatStatus] = useState<{
    currentRevenue: number;
    orderRevenue: number;
    priorRevenue: number;
    percentage: number;
    warning: boolean;
    exceeded: boolean;
    remaining: number;
    year: number;
    limit: number;
    baseLimit: number;
  } | null>(null);

  // ZUS/taxes toggle
  const [includeZusTaxes, setIncludeZusTaxes] = useState(false);
  // Individual tax deduction checkboxes
  const [deductIncomeTax, setDeductIncomeTax] = useState(false);
  const [deductHealthIns, setDeductHealthIns] = useState(false);
  const [deductSocialIns, setDeductSocialIns] = useState(false);

  // COD pending state
  const [codPendingEnabled, setCodPendingEnabled] = useState(false);
  const [codSettlementDays, setCodSettlementDays] = useState(4);
  const [codTransitDays, setCodTransitDays] = useState(2);
  const [codExcludeReturns, setCodExcludeReturns] = useState(true);
  const [codUseExcludedStatuses, setCodUseExcludedStatuses] = useState(false);
  const [codExcludedStatusMap, setCodExcludedStatusMap] = useState<Record<string, string[]>>({});

  // Collector (pozyskiwanie dowodów) state - toggles act immediately, outside the sticky save
  const [collector, setCollector] = useState<FinanceCollectorStatus | null>(null);
  const [collectorBusy, setCollectorBusy] = useState<string | null>(null);

  // Pomiar wejść: sklepy z przypisaną marką marketingową (panel per sklep, działa od razu)
  const [channelStores, setChannelStores] = useState<{ id: string; name: string; marketingBrandId: string }[]>([]);
  const [channelStoresError, setChannelStoresError] = useState(false);
  // storeId -> engineIntegrationId (deep link do banera cookies w Kreatorze)
  const [engineIdByStore, setEngineIdByStore] = useState<Record<string, string>>({});

  // Historia finansów: modal konfiguracji per sklep
  const [journalStore, setJournalStore] = useState<{ id: string; name: string } | null>(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await financeApi.getSettings();
        const res = data as SettingsResponse;
        setCompletedStatusMap(res.completedStatusMap || {});
        setStores(res.stores || []);
        setStatusTree(res.statusTree || []);
        setSnapshotEnabled(res.cashflowSnapshotEnabled || false);
        setVatEnabled(res.vatThresholdEnabled || false);
        setVatLimit(res.vatThresholdLimit || 200000);
        setVatNotifyPercent(res.vatThresholdNotifyPercent || 80);
        setVatStartDate(res.vatThresholdStartDate || '');
        setVatPriorRevenue(res.vatThresholdPriorRevenue || 0);
        setIncludeZusTaxes(res.includeZusTaxes || false);
        if (res.taxDeductions) {
          setDeductIncomeTax(!!res.taxDeductions.incomeTax);
          setDeductHealthIns(!!res.taxDeductions.healthIns);
          setDeductSocialIns(!!res.taxDeductions.socialIns);
        }
        setCodPendingEnabled(!!res.codPendingEnabled);
        if (res.codSettlementDays) setCodSettlementDays(res.codSettlementDays);
        if (res.codTransitDays) setCodTransitDays(res.codTransitDays);
        if (res.codExcludeReturns !== undefined) setCodExcludeReturns(res.codExcludeReturns);
        setCodUseExcludedStatuses(!!res.codUseExcludedStatuses);
        setCodExcludedStatusMap(res.codExcludedStatusMap || {});
      } catch {
        setFeedback({ type: 'error', message: 'Nie udało się wczytać ustawień' });
      } finally {
        setLoading(false);
      }
    })();
    // Collector status is admin-only; for other roles the section stays hidden.
    (async () => {
      try {
        const { data } = await financeApi.getCollectorStatus();
        setCollector(data);
      } catch {
        setCollector(null);
      }
    })();
    // Sklepy z marką marketingową dla sekcji pomiaru wejść
    (async () => {
      try {
        const { data } = await financeChannelApi.stores();
        setChannelStores(data || []);
      } catch {
        setChannelStoresError(true);
      }
    })();
    // Mapowanie storeId -> engineIntegrationId (link do banera cookies w Kreatorze)
    (async () => {
      try {
        const res = await storeApi.getAll();
        const list = ((res.data ?? res) as { id: string; engineIntegrationId?: string | null }[]) || [];
        const map: Record<string, string> = {};
        for (const store of list) {
          if (store.engineIntegrationId) map[store.id] = store.engineIntegrationId;
        }
        setEngineIdByStore(map);
      } catch {
        /* deep link do Kreatora jest opcjonalny */
      }
    })();
  }, []);

  const refreshCollector = async () => {
    const { data } = await financeApi.getCollectorStatus();
    setCollector(data);
  };

  const handleCollectorToggle = async (storeId: string, active: boolean) => {
    setCollectorBusy(storeId);
    setFeedback(null);
    try {
      if (active) await financeApi.disableCollector(storeId);
      else await financeApi.enableCollector(storeId);
      await refreshCollector();
      setFeedback({ type: 'success', message: active ? 'Wyłączono pozyskiwanie dowodów dla sklepu' : 'Włączono pozyskiwanie dowodów dla sklepu' });
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się zmienić pozyskiwania dowodów' });
    } finally {
      setCollectorBusy(null);
    }
  };

  // Fetch VAT threshold status when enabled
  useEffect(() => {
    if (!vatEnabled || loading) return;
    (async () => {
      try {
        const { data } = await financeApi.getVatThreshold();
        if (data && data.enabled) {
          setVatStatus({
            currentRevenue: data.currentRevenue,
            orderRevenue: data.orderRevenue ?? data.currentRevenue,
            priorRevenue: data.priorRevenue ?? 0,
            percentage: data.percentage,
            warning: data.warning,
            exceeded: data.exceeded,
            remaining: data.remaining ?? 0,
            year: data.year,
            limit: data.limit,
            baseLimit: data.baseLimit ?? data.limit,
          });
        }
      } catch {
        // Non-critical
      }
    })();
  }, [vatEnabled, loading]);

  const handleToggle = (storeId: string, statusId: string) => {
    setCompletedStatusMap((prev) => {
      const current = prev[storeId] || [];
      const updated = current.includes(statusId)
        ? current.filter((id) => id !== statusId)
        : [...current, statusId];
      return { ...prev, [storeId]: updated };
    });
  };

  const handleToggleCodExcluded = (storeId: string, statusId: string) => {
    setCodExcludedStatusMap((prev) => {
      const current = prev[storeId] || [];
      const updated = current.includes(statusId)
        ? current.filter((id) => id !== statusId)
        : [...current, statusId];
      return { ...prev, [storeId]: updated };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await financeApi.updateSettings({
        completedStatusMap,
        cashflowSnapshotEnabled: snapshotEnabled,
        vatThresholdEnabled: vatEnabled,
        vatThresholdLimit: vatLimit,
        vatThresholdNotifyPercent: vatNotifyPercent,
        vatThresholdStartDate: vatStartDate || null,
        vatThresholdPriorRevenue: vatPriorRevenue,
        includeZusTaxes,
        taxDeductions: {
          incomeTax: deductIncomeTax,
          healthIns: deductHealthIns,
          socialIns: deductSocialIns,
        },
        codPendingEnabled,
        codSettlementDays,
        codTransitDays,
        codExcludeReturns,
        codUseExcludedStatuses,
        codExcludedStatusMap,
      });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n: number) =>
    n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        icon={Settings}
        title="Ustawienia"
        subtitle="Konfiguracja modułu finansowego"
      />

      {/* Feedback */}
      {feedback && (
        <div
          className={`flex items-center gap-2 p-3 rounded-xl text-sm ${
            feedback.type === 'success'
              ? 'bg-success/10 border border-success/30 text-success'
              : 'bg-danger/10 border border-danger/30 text-danger'
          }`}
        >
          {feedback.type === 'success' ? (
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {feedback.message}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
        </div>
      )}

      {!loading && (
        <div className="space-y-4">
          {/* ================================================================ */}
          {/* SECTION 1: Statusy zamówień                                      */}
          {/* ================================================================ */}
          <CollapsibleSection
            icon={<ClipboardList className="w-5 h-5" />}
            title="Statusy zamówień"
            description="Mapowanie statusu &laquo;Zrealizowane&raquo; dla każdego sklepu"
            defaultOpen={false}
          >
            <p className="text-sm text-text-secondary mt-3 mb-4">
              Wybierz, które statusy zamówień traktować jako zrealizowane.
              Filtr &ldquo;Zrealizowane&rdquo; w Cashflow i Dashboardzie będzie filtrował
              zamówienia tylko do wybranych statusów.
            </p>

            {stores.length > 0 ? (
              <div>
                {stores.map((store) => (
                  <StoreStatusSection
                    key={store.id}
                    store={store}
                    statusTree={statusTree}
                    selectedIds={completedStatusMap[store.id] || []}
                    onToggle={handleToggle}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Store}
                title="Brak aktywnych sklepów"
                description="Dodaj sklep w Integracje, aby skonfigurować statusy."
                size="sm"
              />
            )}
          </CollapsibleSection>

          {/* ================================================================ */}
          {/* SECTION 2: Zamrażanie danych Cashflow                            */}
          {/* ================================================================ */}
          <CollapsibleSection
            icon={<Snowflake className="w-5 h-5" />}
            title="Zamrażanie danych Cashflow"
            description="Automatyczne zapisywanie dziennych danych jako niezmienne"
            defaultOpen={false}
          >
            <div className="mt-4 space-y-4">
              <p className="text-sm text-text-secondary">
                Po włączeniu tej opcji, dane za zakończone dni w arkuszu Cashflow będą
                „zamrożone" - zapisane jako sztywne liczby, które nie będą
                przeliczane ponownie na podstawie nowych danych. Dzięki temu masz pewność,
                że historyczne dane w Cashflow nie zmienią się w przyszłości.
              </p>

              <label className="flex items-center gap-3 p-3 bg-surface-hover/30 rounded-xl cursor-pointer hover:bg-surface-hover/50 transition-colors">
                <input
                  type="checkbox"
                  checked={snapshotEnabled}
                  onChange={(e) => setSnapshotEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                />
                <div>
                  <span className="text-text-primary text-sm font-medium">Włącz zamrażanie danych</span>
                  <p className="text-xs text-text-muted mt-0.5">
                    Zamrożone dni będą oznaczone w arkuszu. Możesz odmrozić dane ręcznie.
                  </p>
                </div>
              </label>

              {snapshotEnabled && (
                <div className="bg-accent-primary/5 border border-accent-primary/20 rounded-xl p-4">
                  <p className="text-xs text-text-secondary">
                    Aby zamrozić dane za dany okres, przejdź do arkusza Cashflow i użyj przycisku
                    zamrażania. Zamrożone dni będą automatycznie używane zamiast danych obliczanych na bieżąco.
                  </p>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* ================================================================ */}
          {/* SECTION 2b: Pozyskiwanie dowodów finansowych                    */}
          {/* ================================================================ */}
          {collector && (
            <CollapsibleSection
              icon={<PackageCheck className="w-5 h-5" />}
              title="Pozyskiwanie dowodów finansowych"
              description="Niezmienne kopie dowodów, dzięki którym raporty potwierdzają kwoty"
              defaultOpen={false}
            >
              <div className="mt-4 space-y-4">
                <p className="text-sm text-text-secondary">
                  System regularnie zapisuje niezmienne kopie dowodów (zamówienia, przesyłki, koszty),
                  dzięki którym raporty Finansów potwierdzają kwoty zamiast pokazywać bieżący stan sklepu.
                  Włączenie nie zmienia nic w sklepie. Możesz wyłączyć w każdej chwili - zachowane dowody zostają.
                </p>

                {!collector.masterEnabled && (
                  <div className="bg-warning/10 border border-warning/30 rounded-xl p-4">
                    <p className="text-sm text-warning">
                      Pozyskiwanie jest wstrzymane globalnie przez administratora platformy.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  {collector.stores.map((store) => (
                    <div
                      key={store.storeId}
                      className="flex items-center gap-3 p-3 bg-surface-hover/30 rounded-xl"
                    >
                      <Store className="w-4 h-4 text-accent-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-text-primary text-sm font-medium">{store.name}</span>
                        <p className="text-xs text-text-muted mt-0.5">
                          {store.active && store.grant
                            ? `Odnawiane automatycznie, ważne do ${new Date(store.grant.expiresAt).toLocaleDateString('pl-PL')}`
                            : 'Wyłączone'}
                        </p>
                      </div>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          store.active ? 'bg-success/15 text-success' : 'bg-surface-hover text-text-muted'
                        }`}
                      >
                        {store.active ? 'Aktywne' : 'Nieaktywne'}
                      </span>
                      <button
                        onClick={() => handleCollectorToggle(store.storeId, store.active)}
                        disabled={!collector.masterEnabled || collectorBusy === store.storeId}
                        className="btn-secondary text-xs flex items-center gap-1.5 disabled:opacity-50"
                      >
                        {collectorBusy === store.storeId && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        {store.active ? 'Wyłącz' : 'Włącz'}
                      </button>
                    </div>
                  ))}
                  {!collector.stores.length && (
                    <p className="text-sm text-text-muted">Brak aktywnych sklepów. Dodaj sklep w Integracje.</p>
                  )}
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* ================================================================ */}
          {/* SECTION 2c: Pomiar wejść w sklepie                              */}
          {/* ================================================================ */}
          <CollapsibleSection
            icon={<MousePointerClick className="w-5 h-5" />}
            title="Pomiar wejść w sklepie"
            description="Skąd klienci trafiają do sklepu - reklama Meta, e-mail czy wejście bezpośrednie"
            defaultOpen={false}
          >
            <div className="mt-4 space-y-4">
              <p className="text-sm text-text-secondary">
                Pomiar zapisuje źródła wejść klientów i łączy je z zamówieniami - wyłącznie po zgodzie
                klienta na cookies analityczne i marketingowe. Włączasz go raz dla każdego sklepu,
                a system automatycznie dociąga wejścia i powiązuje je z zamówieniami raz dziennie,
                bez Twojej pracy.
              </p>
              {channelStoresError && (
                <p className="text-sm text-warning">Nie udało się wczytać listy sklepów. Odśwież stronę.</p>
              )}
              <div className="space-y-2">
                {channelStores.map((store) => (
                  <FinanceAttributionStorePanel
                    key={store.id}
                    storeId={store.id}
                    storeName={store.name}
                    brandId={store.marketingBrandId}
                    kreatorHref={engineIdByStore[store.id] ? `/kreator/${engineIdByStore[store.id]}/pages` : undefined}
                  />
                ))}
                {!channelStoresError && !channelStores.length && (
                  <p className="text-sm text-text-muted">Brak sklepów z przypisaną marką marketingową.</p>
                )}
              </div>
            </div>
          </CollapsibleSection>

          {/* ================================================================ */}
          {/* SECTION 2d: Historia finansów                                   */}
          {/* ================================================================ */}
          <CollapsibleSection
            icon={<History className="w-5 h-5" />}
            title="Historia finansów"
            description="Niezmienny zapis zmian zamówień i zwrotów, potwierdzający kwoty w Finansach"
            defaultOpen={false}
          >
            <div className="mt-4 space-y-4">
              <p className="text-sm text-text-secondary">
                Historia zachowuje zmiany zamówień i zwrotów w sklepie, dzięki czemu raporty Finansów
                potwierdzają kwoty nawet po późniejszych zmianach w WooCommerce. Po włączeniu system
                zapisuje odczyt automatycznie raz dziennie - nie wymaga to żadnej pracy.
              </p>
              <div className="space-y-2">
                {stores.map((store) => (
                  <div key={store.id} className="flex items-center gap-3 p-3 bg-surface-hover/30 rounded-xl">
                    <Store className="w-4 h-4 text-accent-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-text-primary text-sm font-medium">{store.name}</span>
                    </div>
                    <button
                      onClick={() => setJournalStore({ id: store.id, name: store.name })}
                      className="btn-secondary text-xs"
                    >
                      Konfiguruj
                    </button>
                  </div>
                ))}
                {!stores.length && (
                  <p className="text-sm text-text-muted">Brak aktywnych sklepów. Dodaj sklep w Integracje.</p>
                )}
              </div>
            </div>
          </CollapsibleSection>

          {/* ================================================================ */}
          {/* SECTION 3: ZUS i podatki w obliczeniach                         */}
          {/* ================================================================ */}
          <CollapsibleSection
            icon={<Calculator className="w-5 h-5" />}
            title="ZUS i podatki w obliczeniach"
            description="Uwzględnianie składek ZUS, zdrowotnej i podatku dochodowego w cashflow"
            defaultOpen={false}
          >
            <div className="mt-4 space-y-4">
              <p className="text-sm text-text-secondary">
                Domyślnie system liczy czysty zysk z projektu bez uwzględniania składek ZUS,
                zdrowotnej i podatku dochodowego. Włącz tę opcję, jeśli chcesz widzieć
                pełne rozliczenie z uwzględnieniem danin publicznych.
              </p>

              <label className="flex items-center gap-3 p-3 bg-surface-hover/30 rounded-xl cursor-pointer hover:bg-surface-hover/50 transition-colors">
                <input
                  type="checkbox"
                  checked={includeZusTaxes}
                  onChange={(e) => setIncludeZusTaxes(e.target.checked)}
                  className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                />
                <div>
                  <span className="text-text-primary text-sm font-medium">Uwzględniaj ZUS i podatki</span>
                  <p className="text-xs text-text-muted mt-0.5">
                    Gdy włączone, w arkuszu Cashflow i na dashboardzie pojawią się: podatek dochodowy,
                    składka zdrowotna i ZUS społeczny. Zysk netto będzie pomniejszony o te kwoty.
                  </p>
                </div>
              </label>

              {includeZusTaxes && (
                <div className="space-y-2 pl-2 border-l-2 border-accent-primary/20 ml-2">
                  <p className="text-xs text-text-secondary mb-2">Wybierz, które składki i podatki mają być odliczane od zysku netto:</p>
                  {([
                    { key: 'incomeTax', label: 'Podatek dochodowy (PIT)', desc: 'Podatek liniowy, skala podatkowa lub ryczałt', state: deductIncomeTax, setter: setDeductIncomeTax },
                    { key: 'healthIns', label: 'Składka zdrowotna', desc: 'Składka na ubezpieczenie zdrowotne (NFZ)', state: deductHealthIns, setter: setDeductHealthIns },
                    { key: 'socialIns', label: 'ZUS społeczny', desc: 'Składki na ubezpieczenia społeczne (emerytalne, rentowe, chorobowe, wypadkowe)', state: deductSocialIns, setter: setDeductSocialIns },
                  ] as const).map(({ key, label, desc, state, setter }) => (
                    <label key={key} className="flex items-center gap-3 p-3 bg-surface-hover/30 rounded-xl cursor-pointer hover:bg-surface-hover/50 transition-colors">
                      <input
                        type="checkbox"
                        checked={state}
                        onChange={(e) => setter(e.target.checked)}
                        className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                      />
                      <div>
                        <span className="text-text-primary text-sm font-medium">{label}</span>
                        <p className="text-xs text-text-muted mt-0.5">{desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {!includeZusTaxes && (
                <div className="bg-accent-primary/5 border border-accent-primary/20 rounded-xl p-4">
                  <p className="text-xs text-text-secondary">
                    Przy wyłączonej opcji &ldquo;Zysk netto&rdquo; w cashflow = przychód minus koszty
                    (produkty, wysyłka, prowizje, koszty stałe, reklama). Bez ZUS i podatków.
                    Przydatne, gdy prowadzisz firmę i chcesz widzieć czysty cash z tego projektu.
                  </p>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* ================================================================ */}
          {/* SECTION 4: Próg VAT                                             */}
          {/* ================================================================ */}
          <CollapsibleSection
            icon={<ShieldAlert className="w-5 h-5" />}
            title="Próg VAT"
            description="Śledzenie obrotu i powiadomienia o zbliżaniu się do progu"
            defaultOpen={false}
          >
            <div className="mt-4 space-y-4">
              <p className="text-sm text-text-secondary">
                Jeśli korzystasz ze zwolnienia podmiotowego z VAT, ustaw próg obrotu
                i procent, przy którym chcesz otrzymywać ostrzeżenia. System
                automatycznie obliczy Twój bieżący obrót na podstawie zamówień.
              </p>

              <label className="flex items-center gap-3 p-3 bg-surface-hover/30 rounded-xl cursor-pointer hover:bg-surface-hover/50 transition-colors">
                <input
                  type="checkbox"
                  checked={vatEnabled}
                  onChange={(e) => setVatEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                />
                <div>
                  <span className="text-text-primary text-sm font-medium">Włącz śledzenie progu VAT</span>
                  <p className="text-xs text-text-muted mt-0.5">
                    Monitoruj obrót i otrzymuj ostrzeżenia przed przekroczeniem limitu.
                  </p>
                </div>
              </label>

              {vatEnabled && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Roczny limit obrotu (zł)
                      </label>
                      <input
                        type="number"
                        value={vatLimit}
                        onChange={(e) => setVatLimit(parseInt(e.target.value) || 0)}
                        min={0}
                        step={1000}
                        className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:border-accent-primary focus:outline-none"
                      />
                      <p className="text-xs text-text-muted mt-1">
                        Standardowy limit to 200 000 zł rocznie
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Powiadom przy (%)
                      </label>
                      <input
                        type="number"
                        value={vatNotifyPercent}
                        onChange={(e) => setVatNotifyPercent(parseInt(e.target.value) || 80)}
                        min={10}
                        max={100}
                        step={5}
                        className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:border-accent-primary focus:outline-none"
                      />
                      <p className="text-xs text-text-muted mt-1">
                        Ostrzeżenie gdy obrót przekroczy ten % limitu
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Data rozpoczęcia działalności
                      </label>
                      <input
                        type="date"
                        value={vatStartDate}
                        onChange={(e) => setVatStartDate(e.target.value)}
                        className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:border-accent-primary focus:outline-none"
                      />
                      <p className="text-xs text-text-muted mt-1">
                        Limit jest proporcjonalny do liczby miesięcy w roku.
                        Jeśli zaczynasz w czerwcu, limit = {fmt(Math.round(vatLimit * 7 / 12))} zł
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Wcześniejszy przychód (zł)
                      </label>
                      <input
                        type="number"
                        value={vatPriorRevenue}
                        onChange={(e) => setVatPriorRevenue(parseFloat(e.target.value) || 0)}
                        min={0}
                        step={100}
                        className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:border-accent-primary focus:outline-none"
                      />
                      <p className="text-xs text-text-muted mt-1">
                        Przychód wygenerowany przed korzystaniem z ecomflow w tym roku
                      </p>
                    </div>
                  </div>

                  {/* VAT Status indicator */}
                  {vatStatus && (
                    <div
                      className={`rounded-xl p-4 border ${
                        vatStatus.exceeded
                          ? 'bg-danger/10 border-danger/30'
                          : vatStatus.warning
                          ? 'bg-warning/10 border-warning/30'
                          : 'bg-success/5 border-success/20'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-text-primary">
                          Obrót {vatStatus.year}
                        </span>
                        <span
                          className={`text-sm font-bold ${
                            vatStatus.exceeded
                              ? 'text-danger'
                              : vatStatus.warning
                              ? 'text-warning'
                              : 'text-success'
                          }`}
                        >
                          {vatStatus.percentage}%
                        </span>
                      </div>

                      {/* Progress bar */}
                      <div className="w-full h-2 bg-surface-hover rounded-full overflow-hidden mb-2">
                        <div
                          className={`h-full rounded-full transition-all ${
                            vatStatus.exceeded
                              ? 'bg-danger'
                              : vatStatus.warning
                              ? 'bg-warning'
                              : 'bg-success'
                          }`}
                          style={{ width: `${Math.min(vatStatus.percentage, 100)}%` }}
                        />
                      </div>

                      <div className="flex justify-between text-xs text-text-secondary">
                        <span>{fmt(vatStatus.currentRevenue)} zł</span>
                        <span>{fmt(vatStatus.limit)} zł</span>
                      </div>

                      {vatStatus.limit < vatStatus.baseLimit && (
                        <p className="text-xs text-text-muted mt-1">
                          Limit proporcjonalny: {fmt(vatStatus.limit)} zł (pełny roczny: {fmt(vatStatus.baseLimit)} zł)
                        </p>
                      )}

                      {vatStatus.priorRevenue > 0 && (
                        <p className="text-xs text-text-muted mt-1">
                          W tym: {fmt(vatStatus.orderRevenue)} zł z ecomflow + {fmt(vatStatus.priorRevenue)} zł wcześniejszy przychód
                        </p>
                      )}

                      <p className="text-xs text-text-muted mt-1">
                        Pozostało do progu: <span className="text-text-primary font-medium">{fmt(vatStatus.remaining)} zł</span>
                      </p>

                      {vatStatus.exceeded && (
                        <p className="text-xs text-danger mt-2 font-medium">
                          Przekroczono limit! Skontaktuj się z księgowym w sprawie rejestracji do VAT.
                        </p>
                      )}
                      {vatStatus.warning && !vatStatus.exceeded && (
                        <p className="text-xs text-warning mt-2">
                          Zbliżasz się do limitu VAT. Rozważ przygotowanie się do rejestracji.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </CollapsibleSection>

          {/* ================================================================ */}
          {/* SECTION 5: Rozliczenia COD                                      */}
          {/* ================================================================ */}
          <CollapsibleSection
            icon={<Banknote className="w-5 h-5" />}
            title="Rozliczenia COD"
            description="Szacunkowy licznik pobrań oczekujących na przelew od przewoźnika"
            defaultOpen={false}
          >
            <div className="mt-4 space-y-4">
              <p className="text-sm text-text-secondary">
                Po włączeniu na panelu finansowym pojawi się przełącznik &ldquo;Oczekujące COD&rdquo;
                oraz dwa kafelki pokazujące ile pieniędzy z zamówień pobraniowych czeka u przewoźnika
                na rozliczenie. Wartości są szacunkowe i nie uwzględniają faktycznego
                harmonogramu rozliczeń ani niepodjętych paczek - porównuj z wpływami na konto bankowe.
              </p>

              <label className="flex items-center gap-3 p-3 bg-surface-hover/30 rounded-xl cursor-pointer hover:bg-surface-hover/50 transition-colors">
                <input
                  type="checkbox"
                  checked={codPendingEnabled}
                  onChange={(e) => setCodPendingEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                />
                <div>
                  <span className="text-text-primary text-sm font-medium">Włącz licznik oczekujących pobrań</span>
                  <p className="text-xs text-text-muted mt-0.5">
                    Gdy wyłączone, przełącznik i kafelki znikają z panelu finansowego.
                  </p>
                </div>
              </label>

              {codPendingEnabled && (
                <div className="space-y-4 pl-2 border-l-2 border-accent-primary/20 ml-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Dni robocze do wypłaty (T+X)
                      </label>
                      <input
                        type="number"
                        value={codSettlementDays}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          if (Number.isFinite(n)) setCodSettlementDays(Math.min(30, Math.max(1, n)));
                        }}
                        min={1}
                        max={30}
                        className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:border-accent-primary focus:outline-none"
                      />
                      <p className="text-xs text-text-muted mt-1">
                        Ile dni roboczych po dostarczeniu przewoźnik przelewa pobranie. Standard: 3-5 dni,
                        pomijając weekendy i święta.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Średni czas transportu (dni)
                      </label>
                      <input
                        type="number"
                        value={codTransitDays}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          if (Number.isFinite(n)) setCodTransitDays(Math.min(14, Math.max(1, n)));
                        }}
                        min={1}
                        max={14}
                        className="w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:border-accent-primary focus:outline-none"
                      />
                      <p className="text-xs text-text-muted mt-1">
                        Ile dni roboczych od nadania do dostarczenia. Używane do oszacowania paczek
                        w drodze. Standard: 1-3 dni.
                      </p>
                    </div>
                  </div>

                  <label className="flex items-center gap-3 p-3 bg-surface-hover/30 rounded-xl cursor-pointer hover:bg-surface-hover/50 transition-colors">
                    <input
                      type="checkbox"
                      checked={codExcludeReturns}
                      onChange={(e) => setCodExcludeReturns(e.target.checked)}
                      className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                    />
                    <div>
                      <span className="text-text-primary text-sm font-medium">Pomijaj zwroty i niepodjęte</span>
                      <p className="text-xs text-text-muted mt-0.5">
                        Wyklucza paczki oznaczone jako zwrócone lub niedoręczone. Zalecane: włączone -
                        pieniędzy z tych paczek nie otrzymasz.
                      </p>
                    </div>
                  </label>

                  <div className="bg-accent-primary/5 border border-accent-primary/20 rounded-xl p-4">
                    <p className="text-xs text-text-secondary">
                      <span className="text-text-primary font-medium">Jak działają dwa kafelki:</span><br />
                      <span className="text-text-primary">Oczekuje wypłaty</span> - paczki już odebrane przez klientów,
                      czekamy tylko na przelew. Wartość pewniejsza.<br />
                      <span className="text-text-primary">Potencjał COD</span> - wszystkie zamówienia w realizacji,
                      w tym paczki w drodze i (gdy wyłączysz &ldquo;Zrealizowane&rdquo; na panelu)
                      zamówienia jeszcze nie wysłane - klient może zrezygnować.
                    </p>
                  </div>

                  {/* Dodatkowe statusy do pominięcia — opt-in, wyraźny opis co to NIE jest */}
                  <div>
                    <label className="flex items-start gap-3 p-3 bg-surface-hover/30 rounded-xl cursor-pointer hover:bg-surface-hover/50 transition-colors">
                      <input
                        type="checkbox"
                        checked={codUseExcludedStatuses}
                        onChange={(e) => setCodUseExcludedStatuses(e.target.checked)}
                        className="w-4 h-4 mt-0.5 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                      />
                      <div>
                        <span className="text-text-primary text-sm font-medium">Dodatkowo pomijaj wybrane statusy</span>
                        <p className="text-xs text-text-muted mt-1">
                          <span className="text-text-secondary">To nie jest to samo co zwroty i niepodjęte</span> -
                          te wykrywamy automatycznie po statusie paczki u przewoźnika (przełącznik wyżej).
                          Tutaj możesz wskazać <span className="text-text-secondary">dodatkowe statusy zamówień</span>,
                          które mają być wyłączone z kalkulatora - np. &ldquo;braki magazynowe&rdquo;,
                          &ldquo;oczekujące na dostawę&rdquo;, &ldquo;wstrzymane&rdquo;.
                        </p>
                      </div>
                    </label>

                    {codUseExcludedStatuses && (
                      <div className="mt-3 pl-2 border-l-2 border-accent-primary/20 ml-2">
                        {stores.length > 0 ? (
                          <div>
                            {stores.map((store) => (
                              <StoreStatusSection
                                key={store.id}
                                store={store}
                                statusTree={statusTree}
                                selectedIds={codExcludedStatusMap[store.id] || []}
                                onToggle={handleToggleCodExcluded}
                              />
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-text-muted italic">Brak aktywnych sklepów.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleSection>

        </div>
      )}

      {journalStore && (
        <FinanceJournalSettingsModal
          storeId={journalStore.id}
          storeName={journalStore.name}
          onClose={() => setJournalStore(null)}
        />
      )}

      {/* Sticky save bar */}
      <div className="sticky bottom-0 z-30 -mx-1 px-1 pt-4">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-surface-raised/95 backdrop-blur-sm border border-border shadow-lg">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary text-sm flex items-center gap-1.5"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Zapisz ustawienia
          </button>
          {saveStatus === 'success' && (
            <span className="flex items-center gap-1.5 text-xs text-success animate-fade-in">
              <CheckCircle className="w-3.5 h-3.5" /> Zapisano
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="flex items-center gap-1.5 text-xs text-danger animate-fade-in">
              <AlertCircle className="w-3.5 h-3.5" /> Błąd zapisu
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
